/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-argument */

/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import type { FC, ReactNode } from "react";

import { useCallback, useEffect, useState } from "react";

import { message } from "antd";

import type {
  LoginFailResult,
  LoginResult,
} from "@alaffia-technology-solutions/client-sdk";

import type { ChallengeArgs } from "./types";

import { ErrorBoundary } from "../common/error/errorBoundary";
import { useLog } from "../common/log/useLog";
import { LoginCard } from "./loginCard";
import {
  completeLoginDurationMetric,
  startLoginDurationMetric,
} from "./loginMetrics";
import { isChangePwdArgs, isForgotPwdArgs, isMfaCodeArgs } from "./types";
import { useAlaffiaClient } from "./useAlaffiaClient";
import { useLoginStatus } from "./useLoginStatus";

const errMsgForErrResp = (resp: LoginFailResult) => {
  if (resp.httpStatus === 429) {
    return (
      <>
        Account locked because of too many failed login attempts, please contact{" "}
        <a
          href="mailto:support@alaffia.io"
          className="af-text-antd-lt-colorError af-underline"
        >
          support@alaffia.io
        </a>{" "}
        for assistance.
      </>
    );
  }
  return (
    resp?.responseBody?.error_description ??
    resp?.responseBody?.error?.message ??
    "Login failed - verify credentials and try again"
  );
};

export const AlaffiaLogin: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const log = useLog("AlaffiaLogin");

  const alaffiaClient = useAlaffiaClient();
  const { loggedIn, hasLoggedIn, userId } = useLoginStatus();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [lastError, setLastError] = useState<undefined | ReactNode>(undefined);
  const [pendingChallenge, setPendingChallenge] = useState<null | LoginResult>(
    null,
  );
  const [isForgotPassword, setIsForgotPassword] = useState(false);

  useEffect(() => {
    log.trace("loginStatus effect, loggedIn:", {
      loggedIn,
      hasLoggedIn,
      userId,
    });
    if (loggedIn === null) {
      // do nothing - we haven't loaded the client yet to determine if a login can be restored...
    } else if (loggedIn || hasLoggedIn) {
      log.debug("logged in TRUE:", loggedIn);
      if (loggedIn) {
        completeLoginDurationMetric();
      }
      setPendingChallenge(null);
      setLastError(undefined);
      setIsOpen(false);
    } else {
      log.debug("logged in FALSE:", loggedIn);
      setPendingChallenge(null);
      setLastError(undefined);
      setIsOpen(true);
    }
    return () => {
      log.debug("loginStatus EFFECT cleanup", {
        loggedIn,
        hasLoggedIn,
        userId,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn, hasLoggedIn, userId]);

  // todo probably won't need this as we're going to send the user a
  //  new temp password and let them go thru the force-change-pwd flow
  const forgotPassword = useCallback(
    async (user: string) => {
      setIsOpen(false);
      await alaffiaClient.auth
        .forgotPassword({ username: user })
        .then((r: LoginResult) => {
          log.warn("forgotPassword result", {
            r,
            success: r.predicates.isForgotPassword(r),
          });
          if (r.predicates.isCredentialsChangeSuccess(r)) {
            log.trace("password change result", r);

            const msg =
              r.message ??
              "If the account is valid, a reset link will be sent.";

            setPendingChallenge(r);
            setIsOpen(true);
            setIsForgotPassword(false);
            setLastError(undefined);
            message.success(msg);
          } else if (r.predicates.isLoginFail(r)) {
            log.trace("login failed at user/pass", r);
            setLastError(errMsgForErrResp(r));
            setIsOpen(true);
          } else {
            setLastError(undefined);
            setPendingChallenge(r);
            setIsOpen(true);
          }
        })
        .catch((e: unknown) => {
          log.error("Login error", e);
          message.error(
            "Login failed - error communicating with server \n" +
              (typeof e === "string"
                ? e
                : (e as Error)?.message
                  ? (e as Error).message
                  : "(unknown)"),
          );
          setIsOpen(true);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [alaffiaClient],
  );

  const login = useCallback(
    ({ user, pass }: { user: string; pass: string }) => {
      startLoginDurationMetric("password");
      setIsOpen(false);
      alaffiaClient.auth
        .login({
          password: pass,
          username: user,
        })
        .then((r: LoginResult) => {
          if (r.predicates.isLoginSuccess(r)) {
            log.trace("login success", r);
            setLastError(undefined);
            setPendingChallenge(null);
            setIsOpen(false);
          } else if (r.predicates.isLoginFail(r)) {
            log.trace("login failed at user/pass", r);
            setLastError(errMsgForErrResp(r));
            setIsOpen(true);
          } else {
            setLastError(undefined);
            setPendingChallenge(r);
            setIsOpen(true);
          }
        })
        .catch((e) => {
          log.error("Login error", e);
          void message.error(
            "Login failed - error communicating with server \n" +
              (e.message ?? "(unknown)"),
          );
          setIsOpen(true);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [alaffiaClient],
  );

  const challenge = useCallback(
    (args: ChallengeArgs) => {
      setIsOpen(false);
      if (!pendingChallenge) {
        throw new Error("No pending login challenge!");
      } else {
        const fn = pendingChallenge?.predicates.isLoginChallenge(
          pendingChallenge,
        )
          ? async () => {
              if (isMfaCodeArgs(args)) {
                return alaffiaClient.auth.loginChallenge({
                  challengeResponse: pendingChallenge,
                  code: args.code,
                });
              } else {
                throw new Error("MFA code is required");
              }
            }
          : pendingChallenge?.predicates?.isConfigureMfa(pendingChallenge)
            ? async () => {
                if (isMfaCodeArgs(args)) {
                  return alaffiaClient.auth.configureMfa(
                    pendingChallenge,
                    args.code,
                  );
                } else {
                  throw new Error("MFA code is required");
                }
              }
            : pendingChallenge?.predicates?.isForceChangePassword(
                  pendingChallenge,
                )
              ? async () => {
                  if (isChangePwdArgs(args)) {
                    if (args.newPassword !== args.confirmPassword) {
                      throw new Error(
                        "New password and confirm password do not match",
                      );
                    }
                    return alaffiaClient.auth.forceChangePassword({
                      forceChangePasswordResponse: pendingChallenge,
                      newPassword: args.newPassword,
                    });
                  } else {
                    throw new Error(
                      `All fields are required to change password`,
                    );
                  }
                }
              : // todo also probably won't need this as we're going to send the user a
                //  new temp password and let them go thru the force-change-pwd flow
                pendingChallenge?.predicates?.isForgotPassword(pendingChallenge)
                ? async () => {
                    if (isForgotPwdArgs(args)) {
                      if (args.newPassword !== args.confirmPassword) {
                        throw new Error(
                          "New password and confirm password do not match",
                        );
                      }
                      return alaffiaClient.auth.confirmForgotPassword(
                        pendingChallenge.username,
                        args.newPassword,
                        args.code,
                      );
                    } else {
                      throw new Error(
                        `All fields are required to change password`,
                      );
                    }
                  }
                : async () => {
                    return Promise.resolve().then(() => {
                      throw new Error("Unknown pending challenge type");
                    });
                  };

        fn()
          .then((r: LoginResult) => {
            if (r.predicates.isLoginSuccess(r)) {
              log.trace("login success", r);
              setLastError(undefined);
              setPendingChallenge(null);
              setIsOpen(false);
            } else if (r.predicates.isLoginFail(r)) {
              log.error("login failed at user/pass", r);
              setLastError(errMsgForErrResp(r));
              setIsOpen(true);
            } else {
              setLastError(undefined);
              setPendingChallenge(r);
              setIsOpen(true);
            }
          })
          .catch((e) => {
            log.error("login error", e);
            void message.error(
              "Login failed - error communicating with server \n" +
                (e.message ?? "(unknown)"),
            );
            setIsOpen(true);
          });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [alaffiaClient, pendingChallenge],
  );
  return loggedIn === null ? (
    <div className="af-bg-antd-lt-colorBgContainer dark:af-bg-antd-dk-colorBgContainer af-h-full af-max-h-full af-w-full af-max-w-full af-overflow-y-auto"></div>
  ) : !loggedIn && !hasLoggedIn ? (
    <div className="af-fixed af-inset-0">
      <ErrorBoundary boundaryDescription="AlaffiaLogin [never logged in]">
        <LoginCard
          loginResult={pendingChallenge}
          login={login}
          isOpen={isOpen}
          challenge={challenge}
          forgotPassword={forgotPassword}
          lastError={lastError}
          isForgotPassword={isForgotPassword}
          setIsForgotPassword={setIsForgotPassword}
        ></LoginCard>
      </ErrorBoundary>
    </div>
  ) : (
    <div className="af-bg-antd-lt-colorBgContainer dark:af-bg-antd-dk-colorBgContainer af-h-full af-max-h-full af-w-full af-max-w-full af-overflow-y-auto">
      {children}

      <ErrorBoundary boundaryDescription="AlaffiaLogin [not logged in]">
        <LoginCard
          loginResult={pendingChallenge}
          login={login}
          isOpen={isOpen}
          challenge={challenge}
          forgotPassword={forgotPassword}
          lastError={lastError}
          isForgotPassword={isForgotPassword}
          setIsForgotPassword={setIsForgotPassword}
        ></LoginCard>
      </ErrorBoundary>
    </div>
  );
};
